import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Button,
    Typography,
    TextField,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    IconButton,
    Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import logo from "../../styles/logo.png";

// e.g. http://localhost:3001/api/mfa
const MFA_API = process.env.REACT_APP_MFA_API;

const PURPLE = "#4f378b";
const DEEP = "#2b1055";

const primaryButtonSx = {
    py: 1.6,
    borderRadius: "12px",
    background: `linear-gradient(90deg, ${PURPLE}, ${DEEP})`,
    fontWeight: 700,
    textTransform: "none",
    fontSize: "1rem",
    boxShadow: "0 10px 20px rgba(79, 55, 139, 0.3)",
};

const filledFieldSx = {
    "& .MuiFilledInput-root": { borderRadius: "12px", backgroundColor: "#f0f0f7" },
};

/**
 * Mandatory first-time two-step enrollment.
 * Reached with a short-lived setupToken from either /register or /login.
 */
function MfaEnroll() {
    const navigate = useNavigate();
    const location = useLocation();
    const setupToken = location.state?.setupToken;

    // "scan" | "codes"
    const [stage, setStage] = useState("scan");
    const [qrCode, setQrCode] = useState("");
    const [manualKey, setManualKey] = useState("");
    const [code, setCode] = useState("");
    const [codeError, setCodeError] = useState("");
    const [pageError, setPageError] = useState("");
    const [busy, setBusy] = useState(false);

    const [backupCodes, setBackupCodes] = useState([]);
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);

    const call = useCallback(
        async (path, body) => {
            const response = await fetch(`${MFA_API}${path}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${setupToken}`,
                },
                body: JSON.stringify(body || {}),
            });

            let data = {};
            try {
                data = await response.json();
            } catch {
                /* empty body */
            }

            if (!response.ok) {
                const err = new Error(data.message || "Something went wrong. Please try again.");
                err.status = response.status;
                throw err;
            }
            return data;
        },
        [setupToken]
    );

    // Fetch the QR as soon as the page opens.
    useEffect(() => {
         if (!setupToken) {
        setPageError("Setup session missing. Sign in again to restart.");
        return;
    }

        let cancelled = false;

        (async () => {
            try {
                const data = await call("/setup");
                if (cancelled) return;
                setQrCode(data.qrCode);
                setManualKey(data.manualKey);
            } catch (err) {
                if (cancelled) return;
                setPageError(err.message);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [setupToken, call, navigate]);

    const confirm = async (e) => {
        e?.preventDefault();
        if (code.length !== 6) return;

        setBusy(true);
        setCodeError("");
        try {
            const data = await call("/enable", { code });
            setBackupCodes(data.backupCodes || []);
            setSaved(false);
            setCopied(false);
            setStage("codes");
        } catch (err) {
            setCode("");
            if (err.status === 401) {
                setPageError(err.message);
            } else {
                setCodeError(err.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const copyCodes = async () => {
        try {
            await navigator.clipboard.writeText(backupCodes.join("\n"));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopied(false);
        }
    };

    const downloadCodes = () => {
        const body = [
            "Resourcing-Bot backup codes",
            `Generated ${new Date().toLocaleString()}`,
            "Each code works once. Keep this file somewhere safe.",
            "",
            ...backupCodes,
        ].join("\n");
        const url = URL.createObjectURL(new Blob([body], { type: "text/plain" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "resourcing-bot-backup-codes.txt";
        a.click();
        URL.revokeObjectURL(url);
    };

    const goToLogin = () =>
        navigate("/login", {
            replace: true,
            state: { notice: "Two-step verification is on. Sign in to continue." },
        });

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#0f0c29" }}>
            <style>
                {`
                @keyframes meshGradient {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                .mesh-bg {
                    background: linear-gradient(-45deg, #240b36, #4f378b, #0f0c29, #2b1055);
                    background-size: 400% 400%;
                    animation: meshGradient 12s ease infinite;
                }
                `}
            </style>

            {/* Left: branding */}
            <Box
                className="mesh-bg"
                sx={{
                    flex: 1.2,
                    display: { xs: "none", md: "flex" },
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                }}
            >
                <Box sx={{ textAlign: "center", zIndex: 2, p: 4 }}>
                    <Box
                        component="img"
                        src={logo}
                        alt=""
                        sx={{ width: 160, mb: 3, filter: "drop-shadow(0 0 20px rgba(255,255,255,0.3))" }}
                    />
                    <Typography
                        variant="h1"
                        sx={{ fontFamily: "Righteous", fontSize: "3.5rem", color: "#fff", mb: 2 }}
                    >
                        Resourcing-Bot
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 300, letterSpacing: 1.5 }}
                    >
                        One last step to secure your account
                    </Typography>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        width: 300,
                        height: 300,
                        background: "rgba(79, 55, 139, 0.4)",
                        borderRadius: "50%",
                        filter: "blur(80px)",
                        top: "10%",
                        left: "10%",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        width: 400,
                        height: 400,
                        background: "rgba(36, 11, 54, 0.6)",
                        borderRadius: "50%",
                        filter: "blur(100px)",
                        bottom: "10%",
                        right: "10%",
                    }}
                />
            </Box>

            {/* Right: enrollment */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                    background: "#fdfdfd",
                    overflowY: "auto",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 460,
                        background: "#fff",
                        borderRadius: "30px",
                        p: { xs: 3, sm: 5 },
                        boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
                    }}
                >
                    {pageError && (
                        <Alert
                            severity="error"
                            sx={{ mb: 3, borderRadius: "12px" }}
                            action={
                                <Button
                                    size="small"
                                    onClick={() => navigate("/login", { replace: true })}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                    Sign in
                                </Button>
                            }
                        >
                            {pageError}
                        </Alert>
                    )}

                    {/* ---------- Scan ---------- */}
                    {stage === "scan" && (
                        <>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 800, fontFamily: "Righteous", color: DEEP, mb: 1 }}
                            >
                                Set up two-step verification
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                                Every account needs this. Scan the code with Google Authenticator,
                                Microsoft Authenticator, or any TOTP app, then enter the 6-digit code it
                                shows.
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: 236,
                                    p: 2.5,
                                    mb: 2.5,
                                    background: "#f0f0f7",
                                    borderRadius: "16px",
                                }}
                            >
                                {qrCode ? (
                                    <Box
                                        component="img"
                                        src={qrCode}
                                        alt="Authenticator setup code"
                                        sx={{ width: 200, height: 200, borderRadius: "8px", background: "#fff" }}
                                    />
                                ) : (
                                    !pageError && <CircularProgress sx={{ color: PURPLE }} />
                                )}
                            </Box>

                            {manualKey && (
                                <>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
                                    >
                                        Can't scan? Enter this key in your app instead.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            px: 2,
                                            py: 1.25,
                                            mb: 3,
                                            background: "#f0f0f7",
                                            borderRadius: "12px",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: "monospace",
                                                fontSize: "0.9rem",
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            {manualKey}
                                        </Typography>
                                        <Tooltip title="Copy key">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigator.clipboard?.writeText(manualKey)}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </>
                            )}

                            <form onSubmit={confirm}>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    variant="filled"
                                    label="6-digit code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    error={Boolean(codeError)}
                                    helperText={codeError || " "}
                                    inputProps={{
                                        inputMode: "numeric",
                                        autoComplete: "one-time-code",
                                        style: { letterSpacing: "8px", fontSize: "1.2rem" },
                                    }}
                                    sx={filledFieldSx}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={busy || code.length !== 6 || !qrCode}
                                    sx={{ ...primaryButtonSx, mt: 1 }}
                                >
                                    {busy ? (
                                        <CircularProgress size={24} sx={{ color: "#fff" }} />
                                    ) : (
                                        "Verify and continue"
                                    )}
                                </Button>
                            </form>
                        </>
                    )}

                    {/* ---------- Backup codes ---------- */}
                    {stage === "codes" && (
                        <>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <CheckCircleIcon sx={{ color: "#2e7d32" }} />
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 800, fontFamily: "Righteous", color: DEEP }}
                                >
                                    Save your backup codes
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                                These are the only way in if you lose your phone. Each one works once, and
                                this is the only time they'll be shown.
                            </Typography>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 1,
                                    p: 2.5,
                                    mb: 2,
                                    background: "#f0f0f7",
                                    borderRadius: "16px",
                                }}
                            >
                                {backupCodes.map((c) => (
                                    <Typography
                                        key={c}
                                        sx={{
                                            fontFamily: "monospace",
                                            fontSize: "0.95rem",
                                            letterSpacing: "1px",
                                        }}
                                    >
                                        {c}
                                    </Typography>
                                ))}
                            </Box>

                            <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
                                <Button
                                    startIcon={<ContentCopyIcon />}
                                    onClick={copyCodes}
                                    sx={{ textTransform: "none", color: PURPLE, fontWeight: 600 }}
                                >
                                    {copied ? "Copied" : "Copy"}
                                </Button>
                                <Button
                                    startIcon={<DownloadIcon />}
                                    onClick={downloadCodes}
                                    sx={{ textTransform: "none", color: PURPLE, fontWeight: 600 }}
                                >
                                    Download
                                </Button>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={saved}
                                        onChange={(e) => setSaved(e.target.checked)}
                                        sx={{ color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                                    />
                                }
                                label={<Typography variant="body2">I've stored these somewhere safe</Typography>}
                                sx={{ mb: 2 }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                disabled={!saved}
                                onClick={goToLogin}
                                sx={primaryButtonSx}
                            >
                                Continue to sign in
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default MfaEnroll;